import { Box, Stack } from "@mui/material";
import { merge } from "lodash-es";
import { ReactNode, useMemo } from "react";

import AppIconButton from "@/common/components/Button/AppIconButton";
import AppIcon from "@/common/components/Icons/AppIcon";
import AppModal from "@/common/components/Modals/AppModal";
import AppModalContent from "@/common/components/Modals/AppModalContent";
import AppModalTitle from "@/common/components/Modals/AppModalTitle";
import AppPopover from "@/common/components/Popover/AppPopover";
import AppPopoverContent from "@/common/components/Popover/AppPopoverContent";
import AppTab, { AppTabProps } from "@/common/components/Tab/AppTab";
import AppTabs, { AppTabsProps } from "@/common/components/Tab/AppTabs";
import PageTabsManagement from "@/common/components/Tab/PageTabsManagement";
import { UseCommonViewParamsHookResult } from "@/common/hooks/layout/useCommonViewParams";
import {
  PageTabDefinitionList,
  TabIdsDefinition,
  UsePageTabsHookResult,
  usePageTabs,
} from "@/common/hooks/layout/usePageTabs";
import { ViewVariant } from "@/common/ts/layout";
import { ThemeHelper } from "@/theme/helper";

export interface PageTabsRenderFuncParams<TTabId extends string> {
  pageTabs: UsePageTabsHookResult<TTabId>;
  tabsProps: Partial<AppTabsProps>;
  tabProps: Partial<AppTabProps>;
}

interface TabManagementProps {
  isEnabled?: boolean;
  controlVisibility?: "always" | "onHover";
  displayIn?: "popover" | "modal";
}

interface Props<TTabId extends string> {
  /** Tab ids. */
  tabIdsDefinition: TabIdsDefinition<TTabId>;
  /** Tab that is active by default. */
  defaultTabId: TTabId;
  /** All tab definitions. */
  tabs: PageTabDefinitionList<TTabId>;
  /** Display these tabs. */
  onlyTabIds?: TTabId[];
  /** Don't display these tabs. */
  excludeTabIds?: TTabId[];
  /** If `true`, enables tab visibility management. By default enabled if tab persistence is enabled. */
  isTabManagementEnabled?: boolean;
  tabManagement?: TabManagementProps;
  /** viewPage/ listPage: tabs on page.
   * tabular: tabs in DataTabular */
  variant?: "page" | "tabular";
  viewVariant?: ViewVariant;
  /** Integration with `CommonViewParams`. */
  commonViewParams?: UseCommonViewParamsHookResult;
  tabsProps?: Partial<AppTabsProps>;
  tabProps?: Partial<AppTabProps>;
  /** Custom tabs render function. */
  renderTabs?: (tabParams: PageTabsRenderFuncParams<TTabId>) => ReactNode;
  /** Tabs content. */
  children: ((tabParams: UsePageTabsHookResult<TTabId>) => ReactNode) | null;
}

const defaultTabManagementForPage: TabManagementProps = {
  controlVisibility: "always",
  displayIn: "popover",
};
const defaultTabManagementForTabular: TabManagementProps = {
  // controlVisibility: "onHover",
  controlVisibility: "always",
  displayIn: "modal",
};

export default function PageTabsV2<TTabId extends string>({
  tabIdsDefinition: tabsDefinition,
  defaultTabId,
  tabs,
  onlyTabIds,
  excludeTabIds,
  tabManagement,
  variant = "page",
  viewVariant = ViewVariant.Page,
  commonViewParams,
  tabsProps,
  tabProps,
  renderTabs,
  children,
}: Props<TTabId>) {
  const defaultTabManagementComputed = useMemo<TabManagementProps>(
    () =>
      variant === "page"
        ? defaultTabManagementForPage
        : variant === "tabular"
          ? defaultTabManagementForTabular
          : {},
    [variant],
  );
  const tabManagementComputed = useMemo<TabManagementProps>(
    () => merge({}, defaultTabManagementComputed, tabManagement),
    [tabManagement, defaultTabManagementComputed],
  );

  const pageTabs = usePageTabs<TTabId>({
    tabIdsDefinition: tabsDefinition,
    defaultTabId,
    tabs,
    onlyTabIds,
    excludeTabIds,
    isTabManagementEnabled: tabManagementComputed.isEnabled,
    commonViewParams,
  });

  const configTabsButtonClassName = "AppTabsV2-configTabsButton";
  const configTabsButton = (
    <AppIconButton
      className={configTabsButtonClassName}
      sx={{
        ...(tabManagementComputed.controlVisibility === "always" ? {} : undefined),
        ...(tabManagementComputed.controlVisibility === "onHover"
          ? { visibility: "hidden", opacity: 0 }
          : undefined),

        transition: "visibility 0s linear 0s, opacity 300ms",
      }}
      variant='default'
      color='default'
      tooltipProps={{ title: "Configure tabs", enterDelay: 300, enterNextDelay: 300 }}
    >
      <AppIcon of='settings' />
    </AppIconButton>
  );

  return (
    <Stack
      data-testid='PageTabsV2-wrapper1'
      direction='column'
      sx={{
        borderRadius: "inherit",
        ...(tabsProps?.isFullHeight && {
          height: "100%",
        }),
      }}
    >
      {/* Display based on view variants: */}
      {/* Page || Modal - display tabs and content */}
      {/* Tab - display content only */}
      {(viewVariant === ViewVariant.Page ||
        viewVariant === ViewVariant.Modal ||
        variant === "tabular") && (
        <Box
          data-testid='PageTabsV2-wrapper2'
          className='PageTabsV2-tabs'
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(200px, 1fr) 0fr",
            justifyItems: "start",
            alignItems: "center",
            gap: 1,
            maxWidth: "100%",
            // overflow: "hidden",

            ...(tabsProps?.isFullHeight && {
              height: "100%",
            }),
            ...(variant === "page" && {
              borderBottom: (th) => `0.5px solid ${ThemeHelper.getTabsBorderColor(th)}`,
              borderTopLeftRadius: "inherit",
              borderTopRightRadius: "inherit",
              backgroundColor: (t) => t.palette.background.paper,
            }),
            ...(variant === "tabular" && {
              border: "none",
              borderRadius: "inherit",
              backgroundColor: "inherit",
            }),

            [`&:hover .${configTabsButtonClassName}`]: {
              visibility: "visible",
              opacity: 1,
            },
          }}
        >
          {/* <Stack
            data-testid='PageTabsV2-wrapper3'
            direction='row'
            gap={1}
            sx={{
              alignItems: "center",
              ...(tabsProps?.isFullHeight && {
                height: "100%",
              }),
            }}
          > */}
          {renderTabs &&
            renderTabs({
              pageTabs: pageTabs,
              tabsProps: {
                sx: { maxWidth: "100%", minHeight: 0 },
                containerSx: { maxWidth: "100%", minHeight: 0 },
              },
              tabProps: {},
            })}
          {!renderTabs && (
            <AppTabs
              sx={{ maxWidth: "100%", minHeight: 0 }}
              containerSx={{ maxWidth: "100%", minHeight: 0 }}
              value={pageTabs.activeTabId}
              variant='scrollable'
              scrollButtons='auto'
              allowScrollButtonsMobile
              isFullHeight={tabsProps?.isFullHeight}
              onChange={(e, v) => pageTabs.setActiveTab(v)}
              {...tabsProps}
            >
              {pageTabs.visibleTabs.map((tab, i) => (
                <AppTab
                  key={i}
                  label={tab.label}
                  value={tab.value}
                  {...tabProps}
                  {...tab.tabProps}
                />
              ))}
            </AppTabs>
          )}

          {/* Tabs management */}
          {pageTabs.isTabManagementEnabled && (
            <Stack
              data-testid='PageTabsV2-tabManagement'
              direction='row'
              sx={{ pr: 0.5, justifySelf: "flex-end", justifyContent: "flex-end" }}
            >
              {tabManagementComputed.displayIn === "popover" && (
                <AppPopover
                  clickBehavior={{}}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  trigger={configTabsButton}
                >
                  <AppPopoverContent
                    sx={{
                      width: 300,
                      maxWidth: 300,
                      maxHeight: {
                        xxs: "90vh",
                        md: 400,
                      },
                      p: 0, // let the children to handle scroll
                    }}
                    isUseDefaultStyles={false}
                  >
                    <PageTabsManagement
                      manageableTabs={pageTabs.manageableTabs}
                      initialTabVisibilityMap={pageTabs.initialTabVisibilityMap}
                      visibilityMap={pageTabs.tabVisibilityMap}
                      onVisibilityMapChange={(newValue) => {
                        pageTabs.setTabVisibilityMap(newValue);
                      }}
                    />
                  </AppPopoverContent>
                </AppPopover>
              )}

              {tabManagementComputed.displayIn === "modal" && (
                <AppModal fullWidth maxWidth='sm' trigger={configTabsButton}>
                  {(params) => (
                    <>
                      <AppModalTitle onClose={params.close}>Configure tabs</AppModalTitle>
                      <AppModalContent>
                        <PageTabsManagement
                          manageableTabs={pageTabs.manageableTabs}
                          initialTabVisibilityMap={pageTabs.initialTabVisibilityMap}
                          visibilityMap={pageTabs.tabVisibilityMap}
                          onVisibilityMapChange={(newValue) => {
                            pageTabs.setTabVisibilityMap(newValue);
                          }}
                        />
                      </AppModalContent>
                    </>
                  )}
                </AppModal>
              )}
            </Stack>
          )}
          {/* </Stack> */}
        </Box>
      )}

      {children?.(pageTabs)}
    </Stack>
  );
}
